import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarEvent as EngineEvent, HouseholdMember, Role, DataScope } from '@/types';
import { TimeIndicator } from './TimeIndicator';
import { TravelBuffer } from './TravelBuffer';
import { MapPin, Shield, User as UserIcon, Layers } from 'lucide-react';
import clsx from 'clsx';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  format,
  subYears,   // 🌟 ADD THIS
  addYears,    // 🌟 ADD THIS
  startOfYear, // 🌟 ADD THIS
  addMonths,
} from 'date-fns';

interface CalendarGridProps {
  view: 'day' | 'week' | 'month';
  events: EngineEvent[];
  activeMembers: string[];
  isTravelEnabled: boolean;
  isGhostLayerEnabled: boolean;
  hourHeight: number;
  householdMembers: HouseholdMember[];
  currentUserId: string;
  currentHouseholdId: string | null;
  onEventClick: (event: EngineEvent) => void;
  activeScope: DataScope;
  currentDate: Date;
  onViewChange: (newView: 'day' | 'week' | 'month') => void;
  onDateChange: (newDate: Date) => void;   
}

// 🌟 NEW: MonthYearScroller Component (internal to CalendarGrid for now)
const MonthYearScroller: React.FC<{
  currentDate: Date;
  onDateChange: (newDate: Date) => void;
  onViewChange: (newView: 'day' | 'week' | 'month') => void;
}> = ({ currentDate, onDateChange, onViewChange }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const months = React.useMemo(() => {
    const list: Date[] = [];
    const today = new Date();
    const start = subYears(startOfYear(today), 10); // 10 years in the past
    const end = addYears(startOfYear(today), 10);   // 10 years in the future

    let current = startOfMonth(start);
    while (current <= end) {
      list.push(current);
      current = addMonths(current, 1);
    }
    return list;
  }, []);

  return (
    <div
      ref={scrollRef}
      className="w-16 flex-none border-r border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-y-auto no-scrollbar py-2 sticky left-0 z-40"
      style={{ height: '100%' }} // Ensure it takes full height of the scrollable body
    >
      {months.map((monthDate, index) => {
        const isActiveMonth = isSameMonth(monthDate, currentDate);
        return (
          <button
            key={index}
            onClick={() => {
              onDateChange(monthDate);
              onViewChange('month'); // Keep the view as month
            }}
            className={clsx(
              "block w-full text-center py-2 text-[9px] font-black uppercase transition-colors duration-200",
              isActiveMonth
                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 current-month-active"
                : "text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-indigo-500"
            )}
          >
            <span className="block text-[8px] opacity-70">{format(monthDate, 'yyyy')}</span>
            <span className="block">{format(monthDate, 'MMM')}</span>
          </button>
        );
      })}
    </div>
  );
};

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  view,
  events,
  activeMembers,
  isTravelEnabled,
  isGhostLayerEnabled,
  hourHeight = 80, // Base hour height for day/week views
  householdMembers,
  currentUserId,
  currentHouseholdId,
  onEventClick,
  activeScope,
  currentDate,
  onViewChange,
  onDateChange, 
}) => {
  const { t } = useTranslation();
  const calendarGridBodyRef = useRef<HTMLDivElement>(null);
  
  // 🌟 NEW: Define compact and all-day row heights
  const compactHourHeight = 60; // 1/4 reduction from 80px (original hourHeight)
  const allDayRowHeight = 60;   // Height for the all-day events row

  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Helper to check if an event is an all-day event for a specific date (heuristic)
  const isAllDayEventForDate = (event: EngineEvent, date: Date) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    // Consider an event "all-day" if it starts at 00:00, ends at 23:59, and spans a single day
    const startsAtMidnight = eventStart.getHours() === 0 && eventStart.getMinutes() === 0;
    const endsAtEndOfDay = eventEnd.getHours() === 23 && eventEnd.getMinutes() === 59;
    const spansSingleDay = isSameDay(eventStart, eventEnd);

    return isSameDay(eventStart, date) && startsAtMidnight && endsAtEndOfDay && spansSingleDay;
  };

  // 🌟 PHASE 1: Date Generation for Month View
  const daysInMonthView = React.useMemo(() => {
    if (view !== 'month') return [];

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentDate, view]);

  /**
   * 📐 GRID MATH - Adjusted for Day/Week views with all-day row
   */
  const getEventStyle = (event: EngineEvent) => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const durationMinutes = (endDate.getTime() - startDate.getTime()) / 60000;

    return {
      // 🌟 Adjust top to account for allDayRowHeight
      top: `${allDayRowHeight + (startMinutes / 60) * compactHourHeight}px`,
      // 🌟 Use compactHourHeight for event height
      height: `${(durationMinutes / 60) * compactHourHeight}px`,
    };
  };

  // 👥 PERSPECTIVE LOGIC: Adjusted for Personal vs Shared Mode
  const isMultiMember = currentHouseholdId && activeMembers.length > 1 && view === 'day';
  // 🌟 PHASE 2: Adjust columnCount for Month View
  const columnCount = view === 'month' ? 7 : (isMultiMember ? activeMembers.length : (view === 'week' ? 7 : 1));

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (calendarGridBodyRef.current) {
      calendarGridBodyRef.current.scrollTop = 0;
    }
  }, [view, currentDate]);

return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-slate-950 overflow-hidden select-none">

      {/* 🗓️ GRID HEADER: Hybrid Scope Indicators */}
      {/* The ml-16 is applied here to push the header content over, aligning with the actual grid content */}
      <div className={clsx(
        "flex border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 z-50 sticky top-0",
        view === 'month' ? 'ml-16' : 'ml-16' // Keep ml-16 consistent as MonthYearScroller is also w-16
      )}>
        <div
          className="flex-1 grid"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(${isMultiMember ? '300px' : '0'}, 1fr))`,
            width: isMultiMember || view === 'month' ? 'max-content' : '100%'
          }}
        >
          {view === 'month' ? (
            dayLabels.map((day: string) => (
              <div key={day} className="flex-1 py-4 text-center border-l border-slate-50 dark:border-slate-900">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{day}</span>
              </div>
            ))
          ) : isMultiMember ? (
            activeMembers.map((mId: string) => {
              const member = householdMembers.find((h: HouseholdMember) => h.userId === mId);
              return (
                <div key={mId} className="min-w-[300px] py-4 text-center border-l border-slate-50 dark:border-slate-900 flex items-center justify-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-[8px] font-black text-indigo-600 uppercase">
                    {member?.name?.[0] || 'U'}
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">
                    {member?.name || 'User'}
                  </span>
                </div>
              );
            })
          ) : view === 'week' ? (
            dayLabels.map((day: string) => (
              <div key={day} className="flex-1 py-4 text-center border-l border-slate-50 dark:border-slate-900">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{day}</span>
              </div>
            ))
          ) : (
            <div className="flex-1 py-4 px-6 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500 italic">
                {currentHouseholdId ? t('calendar.household_agenda', "Household Agenda") : t('calendar.personal_agenda', "Personal Agenda")}
              </span>
              {/* 🛡️ PRIVACY SHIELD: Visual confirmation of Data Scoping */}
              <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 dark:bg-slate-900 rounded-full border border-slate-100 dark:border-slate-800">
                {activeScope === 'PERSONAL' || !currentHouseholdId ? <UserIcon className="w-3 h-3 text-emerald-500" /> : <Layers className="w-3 h-3 text-indigo-500" />}
                <span className="text-[8px] font-black uppercase tracking-tighter text-slate-400">
                  {currentHouseholdId ? activeScope : 'Personal Mode Active'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🌊 SCROLLABLE BODY */}
      <div className="flex-1 overflow-auto no-scrollbar relative calendar-grid-container">
        {view === 'month' ? ( // Month View Rendering
          <div className="flex h-full w-full">
            <MonthYearScroller currentDate={currentDate} onDateChange={onDateChange} onViewChange={onViewChange} />
            <div
              className="flex-1 grid grid-cols-7 h-full w-full divide-x divide-y border-t border-l border-slate-50 dark:divide-slate-900/30 dark:border-slate-900/30"
              style={{ gridTemplateRows: 'repeat(6, 1fr)' }} // 🌟 Explicitly set gridTemplateRows
            >
              {daysInMonthView.map((day: Date, index: number) => {
                const isToday = isSameDay(day, new Date());
                const isOutsideMonth = !isSameMonth(day, currentDate);
                const eventsForDay = events.filter(event => isSameDay(new Date(event.start), day));

                return (
                  <div
                    key={index}
                    className={clsx(
                      "relative p-2 h-full border-b border-r border-slate-50 dark:border-slate-900/30 flex flex-col items-center cursor-pointer",
                      isOutsideMonth && "opacity-40 text-slate-400 dark:text-slate-600",
                      isToday && "bg-indigo-50 dark:bg-indigo-900/20"
                    )}
                    onClick={() => {
                      onDateChange(day);
                      onViewChange('day');
                    }}
                  >
                    <span className={clsx(
                      "text-[10px] font-black uppercase w-full text-center mb-1",
                      isToday && "text-indigo-600 dark:text-indigo-300"
                    )}>
                      {format(day, 'd')}
                    </span>
                    <div className="flex flex-col items-center gap-1 mt-2 overflow-y-auto no-scrollbar h-[calc(100%-24px)]">
                      {eventsForDay.slice(0, 3).map((event: EngineEvent) => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          className={clsx(
                            "w-full px-2 py-1 rounded-md text-[8px] font-bold text-white truncate cursor-pointer",
                            event.source === 'personal' ? "bg-emerald-600" : "bg-indigo-600"
                          )}
                          title={event.title}
                        >
                          {format(new Date(event.start), 'HH:mm')} - {event.title}
                        </div>
                      ))}
                      {eventsForDay.length > 3 && (
                        <div className="w-full px-2 py-1 rounded-md text-[8px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-700 mt-1">
                          +{eventsForDay.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : ( // Existing Day/Week View rendering
          <div
            className="flex min-h-max relative"
            style={{
              height: `${allDayRowHeight + (24 * compactHourHeight)}px`,
              width: isMultiMember ? 'max-content' : '100%'
            }}
          >
            <div className="absolute top-0 left-16 right-0 z-30 flex" style={{ height: `${allDayRowHeight}px` }}>
              {Array.from({ length: columnCount }).map((_, colIdx: number) => {
                const targetDate = view === 'day' ? currentDate : addDays(startOfWeek(currentDate), colIdx);
                const allDayEventsForColumn = events.filter(event => isAllDayEventForDate(event, targetDate));

                return (
                  <div
                    key={colIdx}
                    className="relative h-full w-full border-b border-r border-slate-100 dark:border-slate-800 p-1 flex flex-col gap-0.5"
                    style={{ minWidth: isMultiMember ? '300px' : '0' }}
                  >
                    {allDayEventsForColumn.map(event => (
                      <div
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className={clsx(
                          "w-full px-2 py-1 rounded-md text-[8px] font-bold text-white truncate cursor-pointer",
                          event.source === 'personal' ? "bg-emerald-600" : "bg-indigo-600"
                        )}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="w-16 flex-none border-r border-slate-100 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm sticky left-0 z-40">
              {/* All-Day label in the time column */}
              <div style={{ height: `${allDayRowHeight}px` }} className="border-b border-slate-100 dark:border-slate-800 flex items-end justify-end pb-1 pr-3">
                 <span className="text-[9px] font-black text-slate-300 dark:text-slate-700 uppercase">
                  All Day
                </span>
              </div>
              {hours.map((hour: number) => (
                <div key={hour} className="relative" style={{ height: `${compactHourHeight}px` }}>
                  <span className="absolute top-1 right-3 text-[9px] font-black text-slate-300 dark:text-slate-700 uppercase">
                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : hour === 0 ? '12 AM' : `${hour} AM`}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="flex-1 relative grid divide-x divide-slate-50 dark:divide-slate-900/30"
              style={{
                gridTemplateColumns: `repeat(${columnCount}, minmax(${isMultiMember ? '300px' : '0'}, 1fr))`
                ,
                width: isMultiMember ? `calc(${columnCount * 300}px)` : '100%',
              }}
            >
              <div className="absolute inset-0 pointer-events-none">
                {hours.map((hour: number) => (
                  <div
                    key={hour}
                    className="w-full border-b border-slate-50 dark:border-slate-900/30"
                    style={{ height: `${compactHourHeight}px` }}
                  />
                ))}
              </div>

              {(view === 'day' || view === 'week') && <TimeIndicator hourHeight={compactHourHeight} topOffset={allDayRowHeight} />}

              {Array.from({ length: columnCount }).map((_, colIdx: number) => (
                <div key={colIdx} className="relative h-full w-full">
                  {events
                    .filter((event: EngineEvent) => {
                      const targetDate = view === 'day' ? currentDate : addDays(startOfWeek(currentDate), colIdx);

                      if (isAllDayEventForDate(event, targetDate)) return false;

                      if (isMultiMember) return event.ownerId === activeMembers[colIdx];

                      if (activeScope === 'PERSONAL') {
                        return event.source === 'personal';
                      }
                      if (activeScope === 'SHARED') {
                        return event.source === 'household';
                      }
                      return true;
                    })
                    .map((event: EngineEvent) => (
                      <div
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className={`absolute left-1.5 right-1.5 group transition-all duration-300 ${event.isSuggestion ? 'opacity-50 z-10' : 'z-20'}`}
                        style={getEventStyle(event)}
                      >
                        {isTravelEnabled && event.location?.address && (
                          <TravelBuffer event={event} hourHeight={compactHourHeight} />
                        )}

                        <div className={clsx(
                          "h-full w-full p-4 rounded-[1.5rem] border shadow-sm flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-xl transition-all",
                          event.source === 'personal' ? "bg-white dark:bg-slate-900 border-emerald-100 dark:border-emerald-900/20" : "bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800"
                        )}>
                          <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-1">
                              {event.source === 'personal' && <UserIcon className="w-2 h-2 text-emerald-500" />}
                              <h4 className="text-[11px] font-black text-slate-900 dark:text-slate-100 leading-tight truncate uppercase italic tracking-tighter">
                                {event.title}
                              </h4>
                            </div>
                            {event.location?.address && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <MapPin className="w-2.5 h-2.5 text-indigo-500" />
                                <p className="text-[8px] font-bold text-slate-400 truncate tracking-tight">
                                  {event.location.address}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex justify-between items-end">
                            {event.isSuggestion && (
                              <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-100 dark:border-amber-900/30">
                                <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                                <span className="text-[7px] font-black uppercase text-amber-600">Proposal</span>
                              </div>
                            )}
                            <div className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-auto tabular-nums">
                              {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};